"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.FauxLocalStorage = exports.ResilientEndpointPoster = void 0;
/*global window */
const utility_1 = require("../common/utility");
const debounce_1 = __importDefault(require("lodash/debounce"));
const web_console_1 = __importDefault(require("./web-console"));
const writer_1 = __importDefault(require("../common/writer"));
const json_1 = __importDefault(require("../formatters/json"));
/**
Log to an endpoint.
 */
let STORAGE_KEY;
const MAX_CACHED_ERRORS = 100; // don't fill up localStorage
const formatter = new json_1.default();
let itemKey = 0;
function getLocalStorage() {
    if (typeof window !== "undefined" && window.localStorage) {
        return window.localStorage;
    }
    else {
        return new FauxLocalStorage();
    }
}
class FauxLocalStorage {
    constructor() {
        this.data = {};
    }
    getItem(key) {
        return this.data[key];
    }
    setItem(key, value) {
        this.data[key] = value;
    }
    removeItem(key) {
        delete this.data[key];
    }
}
exports.FauxLocalStorage = FauxLocalStorage;
class ResilientEndpointPoster {
    constructor(deps = {}) {
        const options = deps.options || {};
        STORAGE_KEY = `lm_error_cache${String(options.url).replace(/\//g, "_")}`;
        this.request = deps.request;
        // fallback in case endpoint logging fails
        this.consoleWriter = deps.consoleWriter || new web_console_1.default();
        this.localStorage = deps.localStorage || getLocalStorage();
        this.maxCacheSize = options.maxCacheSize || MAX_CACHED_ERRORS;
        this.useFetch = options.useFetch || false;
        this.url = options.url;
        this.promise = null;
    }
    post(data) {
        if (!Array.isArray(data)) {
            throw new Error("Data must be an array");
        }
        if (this.promise) {
            this.promise = this.promise.then(() => {
                return this.execute(data);
            });
            return this.promise;
        }
        return this.execute(data);
    }
    updateDeferredData(data) {
        const deferred = this.getDeferredData();
        const withKey = this.addUniqueKey(data);
        const newData = deferred.concat(withKey);
        return this.setDeferredData(newData);
    }
    addUniqueKey(data) {
        return data.map((item) => {
            return [itemKey++, item];
        });
    }
    setDeferredData(dataWithKey) {
        const data = dataWithKey.length <= this.maxCacheSize
            ? dataWithKey
            : dataWithKey.slice(0, this.maxCacheSize);
        const count = data.length;
        this.localStorage.setItem(STORAGE_KEY, JSON.stringify(data).replace(/base64[^"]+/g, "*** base64 image string is stripped in logger ***"));
        return count;
    }
    getDeferredData() {
        const deferred = this.localStorage.getItem(STORAGE_KEY);
        if (deferred) {
            return JSON.parse(deferred);
        }
        return [];
    }
    clearDeferredData() {
        this.localStorage.removeItem(STORAGE_KEY);
    }
    /**
     * Given an array of keys, remove all elements in the passed array
     *
     * @param {Array} keys An array of IDs
     * @returns {void}
     */
    cleanDeferredData(keys) {
        // this.getDeferredData() is an array of [num, log entry object] tuples
        const data = (this.getDeferredData() || []).filter(([num /* entry */]) => !keys.filter((k) => k === num).length); // exclude entries listed in the keys array
        this.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    }
    execute(data) {
        const deferredData = this.getDeferredData();
        const combinedData = data.concat(deferredData.map((e) => e[1]));
        const dataString = "[" + combinedData.map((e) => formatter.format(e)).join(",") + "]";
        let requestPromise = null;
        if (this.useFetch) {
            requestPromise = this.request.post(this.url, {
                headers: { "content-type": "application/json" },
                body: dataString,
            });
        }
        else {
            requestPromise = this.request
                .post(this.url)
                .set("Content-Type", "application/json")
                .send(dataString)
                .end();
        }
        this.promise = requestPromise
            .then(() => {
            this.cleanDeferredData(deferredData.map((e) => e[0]));
            return combinedData.length;
            this.promise = null;
        })
            .catch((err = { message: null, name: null }) => {
            const count = this.updateDeferredData(data);
            let message = `Unable to write ${data.length} log item(s) to url ${this.url}: ${err.message}; deferring ${count} total items.`;
            this.consoleWriter.write({
                level: "info",
                message,
            });
            this.promise = null;
            return 0;
        });
        return this.promise;
    }
}
exports.ResilientEndpointPoster = ResilientEndpointPoster;
class EndpointWriter extends writer_1.default {
    constructor(deps = {}) {
        const options = Object.assign({}, {
            flushDelay: 1000,
        }, deps.options);
        super(options);
        this.request =
            deps.request ||
                utility_1.error("You must provide @costar/request or @costar/fetch as a dependency for Endpo8itnWriter");
        this.flush = debounce_1.default(this.flush, options.flushDelay);
        this.queue = [];
        this.poster = new ResilientEndpointPoster({
            request: this.request,
            consoleWriter: deps.consoleWriter,
            options: {
                url: options.url ||
                    utility_1.error("You must provide a url for EndpointWriter, a POST method that accepts a body { json }"),
                maxCacheSize: options.maxCacheSize,
                useFetch: options.useFetch || false,
            },
        });
    }
    write(obj, callback) {
        if (!super.write(obj))
            return;
        this.queue.push({
            log: obj,
            callback,
        });
        this.flush();
        return true;
    }
    flush() {
        let toPost = [];
        let toNotify = [];
        while (this.queue.length) {
            const item = this.queue.shift();
            toPost.push(item.log);
            if (item.callback) {
                toNotify.push(item.callback);
            }
        }
        return this.poster
            .post(toPost)
            .then(() => {
            toNotify.forEach((e) => e());
        })
            .catch((err) => {
            toNotify.forEach((e) => e(err));
        });
    }
    /**
     * Delete the localStorage cache
     *
     * @returns {void}
     */
    clearLocalStorage() {
        this.poster.clearDeferredData();
    }
}
exports.default = EndpointWriter;
//# sourceMappingURL=endpoint.js.map